import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';
import { useRecoilState } from 'recoil';

import * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { Button, prettifyURL, SegmentedControl, TextSkeleton } from '@gitbook/spine-react';

import { apiQuery } from '../../state';
import { SiteVisitorClaimsButton } from '../AdaptiveContent';
import { getSiteFromOrgAllContent, getSiteSpaceInSiteStructure } from '../Organization';
import { getSiteURLForContent, type SitePreviewContent } from './getSiteURLForContent';
import { sitePreviewAtom } from './state';

interface SitePreviewToolbarProps {
    organizationId: string;
    site: api.Site;
    content: SitePreviewContent;
}

/**
 * Toolbar to control the display of the site preview.
 */
export function SitePreviewToolbar(props: SitePreviewToolbarProps) {
    const { organizationId, site } = props;

    const [state, setState] = useRecoilState(sitePreviewAtom);

    return (
        <div className="flex items-center border-b px-3 py-1">
            <div className="flex flex-1 items-center gap-2">
                <React.Suspense fallback={<TextSkeleton>Loading URL...</TextSkeleton>}>
                    <PreviewURLButton {...props} />
                </React.Suspense>
                {site.adaptiveContent?.enabled ? (
                    <SiteVisitorClaimsButton
                        size="medium"
                        organizationId={organizationId}
                        siteId={site.id}
                    />
                ) : null}
            </div>
            <div className="flex items-center gap-2">
                <SegmentedControl
                    size="small"
                    active={state.theme}
                    onChange={(theme) => setState((prev) => ({ ...prev, theme }))}
                    options={[
                        {
                            value: api.CustomizationThemeMode.Light,
                            label: 'Light',
                            icon: Icon.ThemeLight,
                        },
                        {
                            value: api.CustomizationThemeMode.Dark,
                            label: 'Dark',
                            icon: Icon.ThemeDark,
                        },
                    ]}
                />
                <SegmentedControl
                    size="small"
                    active={state.device}
                    onChange={(device) => setState((prev) => ({ ...prev, device }))}
                    options={[
                        {
                            value: 'desktop',
                            label: 'Desktop',
                            icon: Icon.DeviceDesktop,
                        },
                        {
                            value: 'mobile',
                            label: 'Mobile',
                            icon: Icon.DeviceMobile,
                        },
                    ]}
                />
            </div>
        </div>
    );
}

/**
 * Component to show the URL that can be opened in another tab.
 */
function PreviewURLButton(props: SitePreviewToolbarProps) {
    const { site, organizationId, content } = props;

    const { data: organizationContent } = useSuspenseQuery(
        apiQuery('getOrganizationAllContent', {
            organizationId,
        })
    );

    const url = React.useMemo(() => {
        const siteEntry = getSiteFromOrgAllContent(organizationContent, site.id);
        const siteSpace =
            siteEntry && content.siteSpaceId
                ? getSiteSpaceInSiteStructure(siteEntry.item.structure, content.siteSpaceId)
                : null;

        const rootURL = siteSpace?.urls.published ?? siteEntry?.item.site.urls.published;
        if (!rootURL) {
            return null;
        }

        return getSiteURLForContent(rootURL, {
            siteSpaceId: content.siteSpaceId,
            changeRequestNumber: content.changeRequestNumber,
            revisionId: content.revisionId,
            pagePath: content.pagePath,
        }).toString();
    }, [
        organizationContent,
        site.id,
        content.siteSpaceId,
        content.revisionId,
        content.pagePath,
        content.changeRequestNumber,
    ]);

    if (!site.published) {
        return (
            <Button
                kind="secondary"
                leadingIcon={Icon.Publish}
                href={{
                    screen: {
                        type: 'site',
                        organization: organizationId,
                        site: site.id,
                        tab: undefined,
                    },
                }}
                label="Publish site"
            />
        );
    }

    if (url) {
        return (
            <Button
                kind="blank"
                size="small"
                trailingIcon={Icon.ExternalLink}
                href={url}
                target="_blank"
                label={prettifyURL(url)}
                title="Visit site"
                className="shrink-[5] truncate"
            />
        );
    }

    return null;
}
